import React from "react";
import { FileText, Download, CheckCircle2 } from "lucide-react";
import useReveal from "../hooks/useReveal";
import ContactSection from "../components/ContactSection";
import FAQSection from "../components/FAQSection";
import HeroCover from "../components/HeroCover";
import { TMS_PAGE, IMAGES, META, FAQ_SECTIONS } from "../mock";
import useDocMeta from "../hooks/useDocMeta";

const STRIP = [IMAGES.greenWall, IMAGES.treatmentRoom15856, IMAGES.communityZone];

const TMS_BULLETS = [
  "Beat depression faster - accelerated TMS available",
  "Non-invasive brain stimulation delivered by trained clinicians",
  "Medicare rebates available for eligible clients",
  "Generally well tolerated, low side-effect profile",
  "A non-medication alternative depression treatment",
];

export default function TMS() {
  useReveal();
  useDocMeta(META.tms);
  return (
    <div>
      <HeroCover
        title="TMS for Depression, Anxiety & OCD"
        subtitle="A dedicated and holistic mental health practice and a space for you to slow down, be mindful and enhance your mental wellbeing."
        bullets={TMS_BULLETS}
        badges={[]}
        image={IMAGES.heroClinic}
        defaultHelp="TMS treatment"
        reviewName="Peter de Winter"
        showRibbon={true}
      />

      {/* TMS TREATMENT EXPLAINED — text heading + 4-step timeline */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12 reveal">
            <span className="text-brand-500 font-semibold tracking-[0.2em] text-xs uppercase">AwareHub TMS</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-brand-900 leading-tight">
              {TMS_PAGE.sectionTitle}
            </h2>
          </div>
          <ol className="relative border-l-2 border-brand-200 ml-4 sm:ml-6 space-y-8">
            {TMS_PAGE.steps.map((step, i) => (
              <li key={i} className="reveal pl-8 sm:pl-10" style={{ transitionDelay: `${i * 50}ms` }}>
                <span className="absolute -left-[1.15rem] flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-white text-sm font-semibold shadow-md">
                  {i + 1}
                </span>
                <div className="bg-white rounded-2xl p-6 border border-brand-100 shadow-[0_18px_45px_-40px_rgba(31,56,53,0.5)]">
                  <h3 className="font-display text-xl text-brand-800 mb-2">{step.title}</h3>
                  <p className="text-brand-900/70 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Helpful information */}
      {TMS_PAGE.helpfulInfo && (
        <section className="py-12 sm:py-16 bg-brand-50">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <div className="rounded-[2rem] bg-white border border-brand-200 p-8 sm:p-10 reveal shadow-[0_24px_60px_-48px_rgba(31,56,53,0.5)]">
              <h2 className="font-display text-2xl sm:text-3xl text-brand-800 mb-5">{TMS_PAGE.helpfulInfoTitle}</h2>
              <ul className="space-y-3">
                {TMS_PAGE.helpfulInfo.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-brand-900/80">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-brand-600" />{h}
                  </li>
                ))}
              </ul>
            </div>

            {/* Slim referral form banner/link */}
            <a
              href={TMS_PAGE.referralUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 reveal flex items-center justify-between gap-4 rounded-2xl bg-white border border-brand-200 hover:border-brand-400 px-6 py-4 shadow-sm transition-colors group"
              data-testid="tms-referral-link"
            >
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-brand-100 group-hover:bg-brand-500 flex items-center justify-center transition-colors shrink-0">
                  <FileText className="h-5 w-5 text-brand-600 group-hover:text-white transition-colors" />
                </span>
                <span className="font-semibold text-brand-900">Download a TMS Referral Form for your Doctor</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 shrink-0">
                <Download className="h-4 w-4" /> Download (PDF)
              </span>
            </a>
          </div>
        </section>
      )}

      {/* Horizontal three-image clinic/TMS strip */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-3 gap-3 sm:gap-5 reveal">
            {STRIP.map((src, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-[4/3] shadow-[0_24px_60px_-48px_rgba(31,56,53,0.5)]">
                <img src={src} alt="AwareHub TMS clinic" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection background="bg-brand-50" sections={[FAQ_SECTIONS.tms]} pageTitle="TMS — Frequently Asked Questions" />

      <ContactSection />
    </div>
  );
}
